/**
 * 认证和授权相关类型定义
 * @description 定义用户认证、登录、注册等相关类型和接口
 * @author System
 * @date 2025-07-02
 */

// 导入统一的API响应类型
import type { ApiResponse } from '@/types/api'

// ============== 基础枚举定义 ==============

// 用户角色枚举
export enum UserRole {
  GUEST = 'guest',
  USER = 'user',
  DESIGNER = 'designer',
  MANUFACTURER = 'manufacturer',
  ADMIN = 'admin',
  SUPER_ADMIN = 'super_admin',
}

// 登录方式枚举
export enum LoginType {
  PASSWORD = 'password',
  PHONE = 'phone',
  WECHAT = 'wechat',
}

// 注册方式枚举
export enum RegisterType {
  PHONE = 'phone',
  EMAIL = 'email',
}

// 认证状态枚举
export enum AuthStatus {
  LOGGED_OUT = 'logged_out',
  LOGGING_IN = 'logging_in',
  LOGGED_IN = 'logged_in',
  TOKEN_EXPIRED = 'token_expired',
  REFRESHING_TOKEN = 'refreshing_token',
}

// 验证码类型枚举
export enum CaptchaType {
  IMAGE = 'image',
  SMS = 'sms',
  EMAIL = 'email',
}

// ============== 用户信息接口 ==============

// 用户基础信息接口
export interface BaseUserInfo {
  id: string
  username: string
  email: string
  nickname?: string
  avatar?: string
  phone?: string
  role: UserRole
  isVerified: boolean
  /** 用户积分余额 (用于购买模型等交易) */
  points: number
  createdAt: string
  updatedAt: string
}

// 设计师扩展信息
export interface DesignerProfile {
  skills: string[]
  portfolio: PortfolioItem[]
  experience: number // 工作年限
  rating: number // 评分
  projectCount: number // 项目数量
  certification: string[] // 认证证书
  isCompany: boolean // 是否公司
  companyName?: string
  companySize?: string
}

// 制造商扩展信息
export interface ManufacturerProfile {
  facilities: string[] // 设备信息
  capacity: number // 生产能力
  certifications: string[] // 资质认证
  location: {
    province: string
    city: string
    district?: string
    address?: string
  }
  services: string[] // 服务类型
  isCompany: boolean
  companyName?: string
  businessLicense?: string // 营业执照
}

// 作品集项目
export interface PortfolioItem {
  id: string
  title: string
  description: string
  images: string[]
  tags: string[]
  createdAt: string
  category: string
  isPublic: boolean
}

// 完整的用户信息接口
export interface UserInfo extends BaseUserInfo {
  designerProfile?: DesignerProfile
  manufacturerProfile?: ManufacturerProfile
  lastLoginAt?: string
  loginCount: number
}

// ============== 登录相关接口 ==============

// 图形验证码响应接口
export interface ImageCaptchaResponse {
  captchaId: string
  captchaUrl: string // 修复：改为captchaUrl以匹配实际使用
  expires: number // 过期时间戳
}

// 用户名密码登录凭据
export interface LoginCredentials {
  username: string
  password: string
  imageCaptcha?: string
  captchaId?: string
  rememberMe?: boolean // 记住登录状态
}

// 手机验证码登录凭据
export interface PhoneLoginCredentials {
  phone: string
  code: string
  rememberMe?: boolean
}

// 登录响应数据
export interface LoginResponse {
  token: string
  tokenType: string
  userInfo: UserInfo | null
  permissions?: string[]
  expiresIn?: number // token过期时间(秒)
  loginTime?: number // 登录时间戳
  refreshToken?: string
  /** OAuth2 格式的 access_token，兼容后端不同响应格式 */
  access_token?: string
}

// ============== 注册相关接口 ==============

// 用户注册凭据 - 对齐后端 RegisterData 结构
export interface RegisterCredentials {
  phone: string
  password: string
  code: string // 短信验证码
  captchaId: string
  captchaCode: string // 图形验证码（后端字段名）
}

// 注册响应数据
// 注册成功后直接登录，返回登录凭证
export interface RegisterResponse {
  success: boolean
  userId: string
  message: string
  needVerification?: boolean // 是否需要进一步验证
  // 注册成功后自动登录的凭证
  token?: string
  refreshToken?: string
  userInfo?: UserInfo
  permissions?: string[]
}

// ============== 密码重置相关接口 ==============

// 忘记密码凭据
export interface ForgotPasswordCredentials {
  phone?: string
  email?: string
  verificationCode: string
  newPassword: string
  confirmPassword: string
  captchaId: string
  imageCaptcha: string
}

// 重置密码请求
export interface ResetPasswordRequest {
  token: string // 重置令牌
  newPassword: string
  confirmPassword: string
}

// 用户身份验证数据
export interface VerificationData {
  /** 验证类型: 身份证/营业执照等 */
  type?: 'id_card' | 'business_license' | 'third_party'
  /** 真实姓名 */
  realName?: string
  /** 身份证号 */
  idCard?: string
  /** 第三方平台 */
  platform?: 'wechat' | 'alipay'
  /** 授权码 */
  authCode?: string
}

// ============== 权限相关接口 ==============

// 权限检查结果
export interface PermissionCheckResult {
  hasPermission: boolean
  requiredPermissions: string[]
  userPermissions: string[]
  reason?: string
}

// 角色权限映射
export interface RolePermissionMap {
  [UserRole.GUEST]: string[]
  [UserRole.USER]: string[]
  [UserRole.DESIGNER]: string[]
  [UserRole.MANUFACTURER]: string[]
  [UserRole.ADMIN]: string[]
}

// 权限定义
export interface Permission {
  id: string
  name: string
  description: string
  resource: string // 资源名称
  action: string // 操作类型
  conditions?: Record<string, any> // 权限条件
}

// ============== 会话管理相关接口 ==============

// Token信息
export interface TokenInfo {
  accessToken: string
  refreshToken: string
  tokenType: string
  expiresIn: number
  refreshExpiresIn: number
  issuedAt: number
  /** accessToken 别名，兼容不同 API 响应格式 */
  token?: string
}

// 会话信息
export interface SessionInfo {
  sessionId: string
  userId: string
  deviceId: string
  userAgent: string
  ipAddress: string
  loginTime: number
  lastActiveTime: number
  isActive: boolean
}

// ============== 验证码相关接口 ==============

// 发送验证码请求
export interface SendCodeRequest {
  phone?: string
  email?: string
  type: CaptchaType
  purpose: 'login' | 'register' | 'reset_password' | 'change_phone' | 'change_email'
  captchaId?: string
  imageCaptcha?: string
}

// 验证码验证请求
export interface VerifyCodeRequest {
  phone?: string
  email?: string
  code: string
  type: CaptchaType
  purpose: string
}

// 验证码响应
export interface CodeResponse {
  success: boolean
  message: string
  cooldown?: number // 冷却时间(秒)
  expiresIn?: number // 过期时间(秒)
}

// ============== 用户设置相关接口 ==============

// 用户设置更新请求
export interface UserSettingsUpdate {
  nickname?: string
  avatar?: string
  phone?: string
  email?: string
  oldPassword?: string
  newPassword?: string
  preferences?: UserPreferences
}

// 用户偏好设置
export interface UserPreferences {
  theme: 'light' | 'dark' | 'auto'
  language: 'zh-CN' | 'en-US'
  notifications: {
    email: boolean
    sms: boolean
    push: boolean
    marketing: boolean
  }
  privacy: {
    profileVisible: boolean
    showEmail: boolean
    showPhone: boolean
  }
}

// ============== Store状态接口 ==============

// 认证Store状态
export interface AuthState {
  // 基础状态
  status: AuthStatus
  userInfo: UserInfo | null
  token: string | null
  refreshToken: string | null
  permissions: string[]
  loginTime: number | null

  // 登录相关状态
  loginLoading: boolean
  loginType: LoginType

  // 验证码状态
  imageCaptcha: ImageCaptchaResponse | null
  phoneCodeSending: boolean
  phoneCodeCooldown: number
  emailCodeSending: boolean
  emailCodeCooldown: number

  // 注册状态
  registerLoading: boolean
  registerType: RegisterType

  // 会话状态
  sessions: SessionInfo[]
  currentSessionId: string | null

  // 错误状态
  lastError: string | null
}

// ============== 表单验证相关接口 ==============

// 登录表单数据
export interface LoginFormData {
  username: string
  password: string
  imageCaptcha: string
  rememberMe: boolean
}

// 手机登录表单数据
export interface PhoneLoginFormData {
  phone: string
  code: string
  rememberMe: boolean
}

// 注册表单数据
export interface RegisterFormData {
  phone: string
  email: string
  password: string
  confirmPassword: string
  nickname: string
  code: string
  imageCaptcha: string
  userType: UserRole
  agreeTerms: boolean
  agreePrivacy: boolean
}

// 忘记密码表单数据
export interface ForgotPasswordFormData {
  contact: string // 手机号或邮箱
  contactType: 'phone' | 'email'
  code: string
  newPassword: string
  confirmPassword: string
  imageCaptcha: string
}

// 表单验证规则类型
export type AuthFormRules = {
  [K in keyof (LoginFormData | PhoneLoginFormData | RegisterFormData | ForgotPasswordFormData)]?: import('element-plus').FormItemRule[];
}

// ============== API接口类型 ==============

// 用户API服务接口
export interface UserApiService {
  // 获取用户信息
  getCurrentUser: () => Promise<ApiResponse<UserInfo>>
  getUserById: (id: string) => Promise<ApiResponse<UserInfo>>

  // 更新用户信息
  updateProfile: (data: Partial<UserInfo>) => Promise<ApiResponse<UserInfo>>
  updatePassword: (oldPassword: string, newPassword: string) => Promise<ApiResponse<void>>

  // 头像上传
  uploadAvatar: (file: File) => Promise<ApiResponse<{ url: string }>>

  // 用户设置
  getSettings: () => Promise<ApiResponse<UserPreferences>>
  updateSettings: (settings: Partial<UserPreferences>) => Promise<ApiResponse<void>>
}

// 认证API服务接口
export interface AuthApiService {
  // 登录相关
  login: (credentials: LoginCredentials) => Promise<ApiResponse<LoginResponse>>
  phoneLogin: (credentials: PhoneLoginCredentials) => Promise<ApiResponse<LoginResponse>>
  logout: () => Promise<ApiResponse<void>>

  // 注册相关
  register: (credentials: RegisterCredentials) => Promise<ApiResponse<RegisterResponse>>
  checkUsername: (username: string) => Promise<ApiResponse<{ available: boolean }>>
  checkEmail: (email: string) => Promise<ApiResponse<{ available: boolean }>>
  checkPhone: (phone: string) => Promise<ApiResponse<{ available: boolean }>>

  // 密码重置
  forgotPassword: (credentials: ForgotPasswordCredentials) => Promise<ApiResponse<void>>
  resetPassword: (request: ResetPasswordRequest) => Promise<ApiResponse<void>>

  // 验证码相关
  getImageCaptcha: () => Promise<ApiResponse<ImageCaptchaResponse>>
  sendSmsCode: (request: SendCodeRequest) => Promise<ApiResponse<CodeResponse>>
  sendEmailCode: (request: SendCodeRequest) => Promise<ApiResponse<CodeResponse>>
  verifySmsCode: (request: VerifyCodeRequest) => Promise<ApiResponse<{ valid: boolean }>>
  verifyEmailCode: (request: VerifyCodeRequest) => Promise<ApiResponse<{ valid: boolean }>>

  // Token管理
  refreshToken: (refreshToken: string) => Promise<ApiResponse<TokenInfo>>
  revokeToken: (token: string) => Promise<ApiResponse<void>>

  // 会话管理
  getSessions: () => Promise<ApiResponse<SessionInfo[]>>
  revokeSession: (sessionId: string) => Promise<ApiResponse<void>>
  revokeAllSessions: () => Promise<ApiResponse<void>>
}

// ============== 组件Props接口 ==============

// 登录组件Props
export interface LoginComponentProps {
  defaultType?: LoginType
  showRememberMe?: boolean
  showForgotPassword?: boolean
  showRegisterLink?: boolean
  allowedTypes?: LoginType[]
  redirectTo?: string
}

// 注册组件Props
export interface RegisterComponentProps {
  defaultType?: RegisterType
  allowedTypes?: RegisterType[]
  showLoginLink?: boolean
  redirectTo?: string
  requiredFields?: string[]
}

// 认证布局组件Props
export interface AuthLayoutProps {
  title?: string
  subtitle?: string
  showLogo?: boolean
  showFooter?: boolean
  backgroundImage?: string
  cardMaxWidth?: string
  centerCard?: boolean
}

// ============== 事件接口 ==============

// 认证事件类型
export interface AuthEvents {
  'login-success': LoginResponse
  'login-error': Error
  'logout-success': void
  'register-success': RegisterResponse
  'register-error': Error
  'password-reset-success': void
  'password-reset-error': Error
  'token-expired': void
  'session-expired': void
}

// ============== 工具类型 ==============

// 可选的用户信息更新
export type UserInfoUpdate = Partial<Omit<UserInfo, 'id' | 'createdAt' | 'role'>>

// 登录凭据联合类型
export type LoginCredentialsUnion = LoginCredentials | PhoneLoginCredentials

// 注册验证步骤
export type RegisterStep = 'basic' | 'verification' | 'profile' | 'complete'

// 密码强度等级
export type PasswordStrength = 'weak' | 'medium' | 'strong' | 'very-strong'

// ============== 第三方实名认证接口 ==============

// 第三方认证平台枚举
export enum ThirdPartyPlatform {
  WECHAT = 'wechat',
  ALIPAY = 'alipay',
}

// 实名认证状态枚举
export enum VerificationStatus {
  UNVERIFIED = 'unverified',
  PENDING = 'pending',
  VERIFYING = 'verifying',
  VERIFIED = 'verified',
  FAILED = 'failed',
}

// 用户中心认证状态枚举（三分法）
export enum UserCenterAuthStatus {
  NOT_APPLIED = 'not_applied', // 未申请
  UNDER_REVIEW = 'under_review', // 审核中
  CERTIFIED = 'certified', // 已认证
  REJECTED = 'rejected', // 已拒绝
}

// 模块审核状态类型
export type ModuleReviewStatus = 'not_submitted' | 'under_review' | 'approved' | 'rejected'

// 第三方实名认证请求
export interface ThirdPartyVerificationRequest {
  platform: ThirdPartyPlatform
  redirectUrl?: string // 认证完成后的回调地址
}

// 第三方实名认证响应
export interface ThirdPartyVerificationResponse {
  authUrl: string // 第三方认证页面URL
  orderId: string // 认证订单ID
  expires: number // 过期时间戳
  qrCode?: string // 二维码（可选，用于扫码认证）
}

// 实名认证状态查询响应
export interface VerificationStatusResponse {
  status: VerificationStatus
  platform?: ThirdPartyPlatform
  realName?: string // 仅在认证通过时返回真实姓名
  verifiedAt?: string // 认证完成时间
  orderId?: string
  failReason?: string // 失败原因
}

// 实名认证结果回调
export interface VerificationCallbackData {
  orderId: string
  platform: ThirdPartyPlatform
  status: VerificationStatus
  realName?: string
  verifiedAt?: string
  signature: string // 签名验证
}

// 支付宝实名认证凭据
export interface AlipayVerificationCredentials {
  authCode: string // 支付宝授权码
  state?: string
}

// 微信实名认证凭据
export interface WechatVerificationCredentials {
  code: string // 微信授权码
  state?: string
}

// ============== 用户中心三分法状态系统 ==============

// 三分法状态显示配置
export interface ThreeStateDisplayConfig {
  type: 'info' | 'warning' | 'success' | 'danger'
  text: string
  description?: string
  actionText?: string
}

// 三分法状态映射
export type ThreeStateStatusMap = {
  [key in UserCenterAuthStatus]: ThreeStateDisplayConfig;
}

// 页面显示模式枚举
export enum PageDisplayMode {
  REGISTRATION_FORM = 'registration_form', // 显示注册表单
  PENDING_REVIEW = 'pending_review', // 显示审核等待页面
  WELCOME_GUIDE = 'welcome_guide', // 显示功能引导页面
  REAPPLY_OPTION = 'reapply_option', // 显示重新申请选项
}

// 页面状态逻辑
export interface PageStateLogic {
  authStatus: UserCenterAuthStatus
  displayMode: PageDisplayMode
  allowEdit: boolean
  showDevTools: boolean
}

// 开发环境状态切换器配置
export interface DevStatusSwitcherConfig {
  enabled: boolean
  currentStatus: UserCenterAuthStatus
  availableStatuses: {
    status: UserCenterAuthStatus
    label: string
    description: string
  }[]
}

// 实名认证流程数据
export interface RealNameAuthFlowData {
  realName: string
  authType: 'third_party' | 'manual'
  platform?: ThirdPartyPlatform
  onSuccess?: () => void
  onFailed?: () => void
  onCancel?: () => void
}

// Phase3: 地址信息接口
export interface AddressInfo {
  province: string
  city: string
  district: string
  addressDetail: string
}

// ============== 常量定义 ==============

// 权限常量
export const PERMISSIONS = {
  // 用户相关
  USER_READ: 'user:read',
  USER_UPDATE: 'user:update',
  USER_DELETE: 'user:delete',

  // 模型相关
  MODEL_VIEW: 'model:view',
  MODEL_UPLOAD: 'model:upload',
  MODEL_DOWNLOAD: 'model:download',
  MODEL_EDIT: 'model:edit',
  MODEL_DELETE: 'model:delete',

  // 任务相关
  TASK_VIEW: 'task:view',
  TASK_CREATE: 'task:create',
  TASK_BID: 'task:bid',
  TASK_MANAGE: 'task:manage',

  // 管理员相关
  ADMIN_USER_MANAGE: 'admin:user:manage',
  ADMIN_CONTENT_MANAGE: 'admin:content:manage',
  ADMIN_SYSTEM_CONFIG: 'admin:system:config',
} as const

// 三分法状态常量
export const THREE_STATE_CONFIG: ThreeStateStatusMap = {
  [UserCenterAuthStatus.NOT_APPLIED]: {
    type: 'info',
    text: '未申请',
    description: '请完善信息并提交认证申请',
    actionText: '立即申请',
  },
  [UserCenterAuthStatus.UNDER_REVIEW]: {
    type: 'warning',
    text: '审核中',
    description: '您的申请正在审核中，请耐心等待',
    actionText: '查看进度',
  },
  [UserCenterAuthStatus.CERTIFIED]: {
    type: 'success',
    text: '已认证',
    description: '恭喜！您已通过认证',
    actionText: '管理资料',
  },
  [UserCenterAuthStatus.REJECTED]: {
    type: 'danger',
    text: '已拒绝',
    description: '很抱歉，您的申请未通过审核',
    actionText: '重新申请',
  },
} as const

// 页面显示模式映射
export const PAGE_MODE_MAP: Record<UserCenterAuthStatus, PageDisplayMode> = {
  [UserCenterAuthStatus.NOT_APPLIED]: PageDisplayMode.REGISTRATION_FORM,
  [UserCenterAuthStatus.UNDER_REVIEW]: PageDisplayMode.PENDING_REVIEW,
  [UserCenterAuthStatus.CERTIFIED]: PageDisplayMode.WELCOME_GUIDE,
  [UserCenterAuthStatus.REJECTED]: PageDisplayMode.REAPPLY_OPTION,
} as const

// 错误码常量
export const AUTH_ERROR_CODES = {
  // 成功
  SUCCESS: '00000',

  // 业务错误 (B1xxx)
  INVALID_CREDENTIALS: 'B1001',
  USER_NOT_FOUND: 'B1002',
  USER_DISABLED: 'B1003',
  CAPTCHA_INVALID: 'B1004',
  CAPTCHA_EXPIRED: 'B1005',
  CODE_INVALID: 'B1006',
  CODE_EXPIRED: 'B1007',
  PASSWORD_TOO_WEAK: 'B1008',
  USERNAME_EXISTS: 'B1009',
  EMAIL_EXISTS: 'B1010',
  PHONE_EXISTS: 'B1011',

  // 认证错误 (A1xxx)
  TOKEN_INVALID: 'A1001',
  TOKEN_EXPIRED: 'A1002',
  REFRESH_TOKEN_INVALID: 'A1003',
  PERMISSION_DENIED: 'A1004',
  SESSION_EXPIRED: 'A1005',

  // 系统错误 (S1xxx)
  SYSTEM_ERROR: 'S1001',
  SERVICE_UNAVAILABLE: 'S1002',
  RATE_LIMIT_EXCEEDED: 'S1003',
} as const

// ============== 账号安全相关接口 ==============

// 修改密码请求
export interface ChangePasswordRequest {
  oldPassword: string
  newPassword: string
  confirmPassword: string
}

// 绑定手机请求
export interface BindPhoneRequest {
  phone: string
  code: string
  password?: string // 首次绑定可能需要密码验证
}

// 解绑手机请求
export interface UnbindPhoneRequest {
  code: string // 验证码 (发送到当前绑定手机)
  password?: string // 可选密码验证
}

// 绑定邮箱请求
export interface BindEmailRequest {
  email: string
  code: string
}

// 解绑邮箱请求
export interface UnbindEmailRequest {
  code: string
  password?: string
}

// 发送绑定验证码请求
export interface SendBindingCodeRequest {
  target: string // 手机号或邮箱
  type: 'phone' | 'email'
  purpose: 'bind' | 'unbind' | 'change'
}

// 微信二维码响应
export interface WechatQrCodeResponse {
  qrCodeUrl: string
  sceneId: string
  expiresIn: number
}

// 微信绑定状态响应
export interface WechatBindStatusResponse {
  status: 'waiting' | 'scanned' | 'confirmed' | 'expired'
  nickname?: string
  avatar?: string
}

// 微信登录结果响应
export interface WechatLoginResultResponse extends WechatBindStatusResponse {
  loginResult?: LoginResponse // 包含 token 和 userInfo
}

// 支付宝绑定URL响应
export interface AlipayBindUrlResponse {
  authUrl: string
  state: string
}

// 账号绑定信息响应
export interface AccountBindingInfo {
  phone?: string // 已绑定手机 (脱敏)
  email?: string // 已绑定邮箱 (脱敏)
  wechat?: {
    nickname: string
    avatar?: string
    boundAt: string
  }
  alipay?: {
    nickname: string
    boundAt: string
  }
}

// ============== 密码重置验证相关接口 ==============

// 密码重置验证请求
export interface PasswordResetVerifyRequest {
  contact: string // 手机号或邮箱
  code: string // 验证码
  contactType: 'phone' | 'email'
}

// 密码重置验证响应
export interface PasswordResetVerifyResponse {
  token: string // 重置密码token
  expiresIn: number // token有效期(秒)
}

// ============== 类型重新导出 ==============

// 为了方便使用，重新导出常用类型
export type {
  ApiResponse,
  PaginatedResponse,
} from '@/types/api'
